import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Card, Button, Tag, Spin, Empty, Pagination, Select, message } from 'antd';
import { ShoppingCartOutlined } from '@ant-design/icons';
import { productAPI, cartAPI } from '../services/api';
import { isAuthenticated } from '../utils/auth';
import './Mall.css';

const { Meta } = Card;
const { Option } = Select;

function Mall() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingToCart, setAddingToCart] = useState({});
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 12,
    total: 0
  });
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedType, setSelectedType] = useState('physical');

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    loadProducts();
  }, [pagination.current, selectedCategory, selectedType]);

  const loadCategories = async () => {
    try {
      const res = await productAPI.getCategories();
      setCategories(res.data);
    } catch (error) {
      message.error('加载分类失败');
    }
  };

  const loadProducts = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        limit: pagination.pageSize,
        ...(selectedCategory && { category_id: selectedCategory }),
        ...(selectedType && { type: selectedType })
      };
      const res = await productAPI.getProducts(params);
      setProducts(res.data.products);
      setPagination(prev => ({
        ...prev,
        total: res.data.pagination.total
      }));
    } catch (error) {
      message.error('加载商品失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async (e, productId) => {
    e.stopPropagation();

    if (!isAuthenticated()) {
      message.warning('请先登录');
      navigate('/login');
      return;
    }

    setAddingToCart(prev => ({ ...prev, [productId]: true }));
    try {
      await cartAPI.addToCart({ product_id: productId, quantity: 1 });
      window.dispatchEvent(new Event('cartUpdate'));
      message.success('已加入购物车，正在跳转购物车');
      navigate('/cart');
    } catch (error) {
      message.error(error.message || '添加失败');
    } finally {
      setAddingToCart(prev => ({ ...prev, [productId]: false }));
    }
  };

  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, current: page }));
  };

  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleTypeChange = (value) => {
    setSelectedType(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleProductClick = (id) => {
    navigate(`/mall/${id}`);
  };

  const getTypeTag = (type) => {
    const typeMap = {
      physical: { color: 'blue', text: '实物' },
      virtual: { color: 'purple', text: '虚拟' },
      service: { color: 'green', text: '服务' }
    };
    return typeMap[type] || typeMap.physical;
  };

  return (
    <div className="mall-container">
      <div className="mall-header">
        <h1 className="page-title">情绪商城</h1>
        <p className="page-subtitle">情绪陪伴周边、疗愈玩偶与减压工具</p>
      </div>

      <div className="mall-filters">
        <Select
          placeholder="选择分类"
          style={{ width: 200 }}
          onChange={handleCategoryChange}
          allowClear
        >
          {categories.map(cat => (
            <Option key={cat.id} value={cat.id}>{cat.name}</Option>
          ))}
        </Select>

        <Select
          placeholder="商品类型"
          style={{ width: 150, marginLeft: 16 }}
          value={selectedType}
          onChange={handleTypeChange}
          allowClear={false}
        >
          <Option value="physical">实物商品</Option>
        </Select>
      </div>

      {loading ? (
        <div className="loading-container">
          <Spin size="large" tip="加载中..." />
        </div>
      ) : products.length === 0 ? (
        <Empty description="暂无商品" />
      ) : (
        <>
          <Row gutter={[24, 24]}>
            {products.map(product => {
              const typeInfo = getTypeTag(product.type);
              return (
                <Col xs={24} sm={12} md={8} lg={6} key={product.id}>
                  <Card
                    hoverable
                    cover={
                      <div className="product-cover">
                        <img
                          alt={product.name}
                          src={product.main_image || '/images/default-product.svg'}
                          onError={(e) => { e.target.src = '/images/default-product.svg'; }}
                        />
                        <Tag color={typeInfo.color} className="type-tag">
                          {typeInfo.text}
                        </Tag>
                      </div>
                    }
                    onClick={() => handleProductClick(product.id)}
                    actions={[
                      <Button
                        type="primary"
                        icon={<ShoppingCartOutlined />}
                        loading={addingToCart[product.id]}
                        onClick={(e) => handleAddToCart(e, product.id)}
                        disabled={product.stock === 0}
                        block
                      >
                        {product.stock === 0 ? '已售罄' : '加入购物车'}
                      </Button>
                    ]}
                  >
                    <Meta
                      title={
                        <div className="product-title" title={product.name}>
                          {product.name}
                        </div>
                      }
                      description={
                        <div className="product-meta">
                          <div className="product-price">
                            <span className="current-price">¥{product.price}</span>
                            {product.original_price && product.original_price > product.price && (
                              <span className="original-price">¥{product.original_price}</span>
                            )}
                          </div>
                          <div className="product-stats">
                            <span>库存: {product.stock}</span>
                            <span>已售: {product.sales || 0}</span>
                          </div>
                        </div>
                      }
                    />
                  </Card>
                </Col>
              );
            })}
          </Row>

          {pagination.total > pagination.pageSize && (
            <div className="pagination-container">
              <Pagination
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={pagination.total}
                onChange={handlePageChange}
                showSizeChanger={false}
                showTotal={(total) => `共 ${total} 件商品`}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default Mall;
